/**
 * Object Inspector GraphQL Queries
 *
 * Queries for fetching object details by global ID in the admin object browser.
 *
 * Feature: 062-graphql-api
 */

import { gql } from '@apollo/client';

/**
 * Fragment for common fields all objects share
 */
const COMMON_FIELDS = `
  id
  globalId
  createdAt
`;

/**
 * User query with all fields
 */
export const GET_USER = gql`
  query GetUser($id: ID!) {
    user(id: $id) {
      ${COMMON_FIELDS}
      username
      displayName
      email
      updatedAt
      photos(limit: 10) {
        items {
          id
          title
        }
        totalCount
      }
      collections(limit: 10) {
        items {
          id
          title
        }
        totalCount
      }
      critiques(limit: 10) {
        items {
          id
          content
        }
        totalCount
      }
      critiqueSessions(limit: 10) {
        items {
          id
          title
        }
        totalCount
      }
    }
  }
`;

/**
 * Photo query with all fields
 */
export const GET_PHOTO = gql`
  query GetPhoto($id: ID!) {
    photo(id: $id) {
      ${COMMON_FIELDS}
      userId
      title
      description
      filename
      fileHash
      metadata {
        width
        height
        format
        fileSize
        camera
        lens
        iso
        aperture
        shutterSpeed
        focalLength
        dateTaken
      }
      updatedAt
      user {
        id
        username
        displayName
      }
      variants {
        id
        variantType
        storageUrl
        publicUrl
        width
        height
      }
      collections(limit: 10) {
        items {
          id
          title
        }
        totalCount
      }
      critiques(limit: 10) {
        items {
          id
          content
        }
        totalCount
      }
    }
  }
`;

/**
 * Collection query with all fields
 */
export const GET_COLLECTION = gql`
  query GetCollection($id: ID!) {
    collection(id: $id) {
      ${COMMON_FIELDS}
      userId
      title
      description
      slug
      layout
      updatedAt
      user {
        id
        username
        displayName
      }
      items(limit: 10) {
        items {
          id
          photoId
        }
        totalCount
      }
      critiqueSessions(limit: 10) {
        items {
          id
          title
        }
        totalCount
      }
    }
  }
`;

/**
 * Critique query with all fields
 */
export const GET_CRITIQUE = gql`
  query GetCritique($id: ID!) {
    critique(id: $id) {
      ${COMMON_FIELDS}
      userId
      photoId
      sessionId
      content
      parentId
      editId
      updatedAt
      user {
        id
        username
        displayName
      }
      photo {
        id
        title
      }
      session {
        id
        title
      }
      edits {
        id
        editType
      }
    }
  }
`;

/**
 * CritiqueSession query with all fields
 */
export const GET_CRITIQUE_SESSION = gql`
  query GetCritiqueSession($id: ID!) {
    critiqueSession(id: $id) {
      ${COMMON_FIELDS}
      collectionId
      title
      updatedAt
      collection {
        id
        title
      }
      critiques(limit: 10) {
        items {
          id
          userId
        }
        totalCount
      }
    }
  }
`;

/**
 * CollectionItem query with all fields
 */
export const GET_COLLECTION_ITEM = gql`
  query GetCollectionItem($id: ID!) {
    collectionItem(id: $id) {
      ${COMMON_FIELDS}
      collectionId
      photoId
      position
      caption
      layout
    }
  }
`;

/**
 * CritiqueEdit query with all fields
 */
export const GET_CRITIQUE_EDIT = gql`
  query GetCritiqueEdit($id: ID!) {
    critiqueEdit(id: $id) {
      ${COMMON_FIELDS}
      critiqueId
      parentEditId
      editType
      edits
      updatedAt
    }
  }
`;

/**
 * Notification query with all fields
 */
export const GET_NOTIFICATION = gql`
  query GetNotification($id: ID!) {
    notification(id: $id) {
      ${COMMON_FIELDS}
      userId
      type
      title
      message
      url
      isRead
      user {
        id
        username
      }
    }
  }
`;

/**
 * Map object type prefixes to their queries
 */
export const OBJECT_QUERIES: Record<string, any> = {
  users: GET_USER,
  photos: GET_PHOTO,
  collections: GET_COLLECTION,
  collection_items: GET_COLLECTION_ITEM,
  critiques: GET_CRITIQUE,
  critique_edits: GET_CRITIQUE_EDIT,
  critique_sessions: GET_CRITIQUE_SESSION,
  notifications: GET_NOTIFICATION,
};

/**
 * Map object type prefixes to result field names
 */
export const RESULT_FIELD_NAMES: Record<string, string> = {
  users: 'user',
  photos: 'photo',
  collections: 'collection',
  collection_items: 'collectionItem',
  critiques: 'critique',
  critique_edits: 'critiqueEdit',
  critique_sessions: 'critiqueSession',
  notifications: 'notification',
};

/**
 * Type definitions for query results
 */
export interface ObjectQueryResult {
  __typename: string;
  id: string;
  globalId: string;
  createdAt: string;
  updatedAt?: string;
  [key: string]: any;
}
